import React, { useMemo } from "react";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { User } from "@common/types/user";
import { getTaskAssigneeDisplayName } from "@components/tasks/task-assignee.lib";
import { HStack } from "@design-system/HStack/HStack";

interface TaskAssigneeSelectProps {
    value: User["id"] | null | undefined;
    onChange: (id: User["id"] | null) => void;
    buttonProps?: TriggerButtonProps;
}

export const TaskAssigneeSelect: React.FC<TaskAssigneeSelectProps> = ({
    value,
    onChange,
    buttonProps,
}) => {
    const { data: members = [], isFetched } = useCurrentWorkspaceMembers();

    const membersById = useMemo(
        () => new Map(members.map((member) => [member.user.id, member])),
        [members],
    );
    const items = useMemo(
        (): Array<PickerItemData<User["id"]>> =>
            members.map((member) => ({
                value: member.user.id,
                label: getTaskAssigneeDisplayName(member.user),
                leading: (
                    <UserAvatar user={member.user} size="xs" aria-hidden />
                ),
            })),
        [members],
    );

    const { variant, size, block, ...restButtonProps } = buttonProps ?? {};

    return (
        <Picker.Select
            items={items}
            searchable
            searchPlaceholder="Search members..."
            placeholder="Assign member"
            emptyLabel="No workspace members"
            status={isFetched ? "idle" : "loading"}
            loadingLabel="Loading..."
            value={value ?? null}
            onValueChange={(id) => {
                if (id != null) {
                    onChange(id);
                }
            }}
            renderValue={([id]) => {
                const member = membersById.get(id);
                if (!member) {
                    return undefined;
                }
                return (
                    <HStack gap={8} align="center">
                        <UserAvatar user={member.user} size="sm" aria-hidden />
                        {getTaskAssigneeDisplayName(member.user)}
                    </HStack>
                );
            }}
            trigger={
                <Picker.Button
                    {...restButtonProps}
                    variant={variant ?? "primary"}
                    size={size ?? "md"}
                    block={block ?? true}
                />
            }
        />
    );
};
